import ColissimoIcon from "assets/img/carrier-icons/colissimo.svg";
import MondialRelayIcon from "assets/img/carrier-icons/mondial_relay.svg";
import ClickAndCollectIcon from "assets/img/carrier-icons/click_and_collect.svg";
import DpdIcon from "assets/img/carrier-icons/dpd.svg";
import KuehneNagelIcon from "assets/img/carrier-icons/kuehne_nagel.svg";
import UpsIcon from "assets/img/carrier-icons/ups.svg";

// Tracking number used for ClickAndCollect methods
export const NoTracking = 'no-tracking';

export const ShippingMethods = {
    CLICK_COLLECT: 'click-collect',
    DRIVE: 'drive',
    LOCKER: 'locker'
}

export const ShippingMethodsDiscr = {
    COLISSIMO: "colissimo",
    COLISSIMO_VIA_TOOPOST: "colissimoViaToopost",
    CLICK_COLLECT: "clickAndCollect",
    DRIVE: "drive",
    BIKE: "bike",
    BPOST: "bpost",
    MONDIAL_RELAY: "mondialRelay",
    MONDIAL_RELAY_V2: "mondialRelayV2",
    MONDIAL_RELAY_VIA_TOOPOST: "mondialRelayViaToopost",
    LOCKER: "locker",
    POSTNL: "postNL",
    BRING: "bring",
    HEAVY_DELIVERY: "heavyDelivery",
    GEODIS: "geodis",
    NSHIFT: "nshift",
    UPS: "ups",
    KN: "kuehneNagel",
    DPD: "dpd",
    toArray() {
        return Object.values(this).filter(value => typeof value === 'string');
    }
};

// Who carries the parcel, independent of the discr (a Kuehne+Nagel method can be delivered by Chronopost).
// Values match the API's App\Enum\ShippingMethod\Carrier.
export const Carriers = {
    BPOST: 'bpost',
    BRING: 'bring',
    C_CHEZ_VOUS: 'c_chez_vous',
    CHRONOPOST: 'chronopost',
    COLISSIMO: 'colissimo',
    DPD: 'dpd',
    GEODIS: 'geodis',
    KUEHNE_NAGEL: 'kuehne_nagel',
    MATKAHUOLTO: 'matkahuolto',
    MONDIAL_RELAY: 'mondial_relay',
    POSTNL: 'postnl',
    POSTI: 'posti',
    UPS: 'ups',

    toArray() {
        return Object.values(this).filter(value => typeof value === 'string');
    }
};

// How the customer receives the parcel, orthogonal to the carrier. Values match App\Enum\ShippingMethod\DeliveryMode.
export const DeliveryModes = {
    HOME: 'home',
    PICKUP_POINT: 'pickup_point',
    LOCKER: 'locker',
    CLICK_AND_COLLECT: 'click_and_collect',
    DRIVE: 'drive',

    toArray() {
        return Object.values(this).filter(value => typeof value === 'string');
    }
};

// What choosing a method type presets in the form: these types say how the customer gets the parcel,
// with no carrier involved.
export const DeliveryPresetsByDiscr = {
    [ShippingMethodsDiscr.DRIVE]: {deliveryMode: DeliveryModes.DRIVE, carrier: null},
    [ShippingMethodsDiscr.CLICK_COLLECT]: {deliveryMode: DeliveryModes.CLICK_AND_COLLECT, carrier: null},
};

// Sylius only accepts these modes on the shop name of the same value, and requires them on it.
export const DeliveryModesRequiringShopName = [DeliveryModes.DRIVE, DeliveryModes.LOCKER];

// Outcome of the last push of the method to Sylius, values of App\Enum\ShippingMethod\SyncState.
export const SyncStates = {
    PENDING: 'pending',
    SYNCED: 'synced',
    ERROR: 'error',
};

export const ShippingMethodsWaybillRequired = [
    ShippingMethodsDiscr.COLISSIMO,
    ShippingMethodsDiscr.COLISSIMO_VIA_TOOPOST,
    ShippingMethodsDiscr.BPOST,
    ShippingMethodsDiscr.MONDIAL_RELAY,
    ShippingMethodsDiscr.MONDIAL_RELAY_VIA_TOOPOST,
    ShippingMethodsDiscr.POSTNL,
    ShippingMethodsDiscr.NSHIFT,
    ShippingMethodsDiscr.KN,
    ShippingMethodsDiscr.DPD
];

// Returns the carrier icon (asset URL) for a shipping method discriminator,
// or null when no dedicated icon exists for it.
export function getShippingMethodIconSrc(discr) {
    switch (discr) {
        case ShippingMethodsDiscr.COLISSIMO:
        case ShippingMethodsDiscr.COLISSIMO_VIA_TOOPOST:
            return ColissimoIcon;
        case ShippingMethodsDiscr.MONDIAL_RELAY:
        case ShippingMethodsDiscr.MONDIAL_RELAY_VIA_TOOPOST:
            return MondialRelayIcon;
        case ShippingMethodsDiscr.CLICK_COLLECT:
            return ClickAndCollectIcon;
        case ShippingMethodsDiscr.DPD:
            return DpdIcon;
        case ShippingMethodsDiscr.KN:
            return KuehneNagelIcon;
        case ShippingMethodsDiscr.UPS:
            return UpsIcon;
        default:
            return null;
    }
}

// Shipping methods whose relay point can be changed from the order view
// (mirrors the providers registered behind GET/PUT /shipments/{id}/relay_point on the API).
export const RelayPointChangeableDiscr = [
    ShippingMethodsDiscr.MONDIAL_RELAY,
    ShippingMethodsDiscr.MONDIAL_RELAY_V2,
    ShippingMethodsDiscr.MONDIAL_RELAY_VIA_TOOPOST,
];

/**
 * Resolve the Doctrine discriminator ("mondialRelayV2", "colissimo"…) of a serialized shipping method.
 *
 * `discr` is only exposed in some serialization groups; the JSON-LD `@type` (short class name) is always
 * there for an embedded resource, and lcfirst(@type) equals the discriminator by convention.
 */
export function getShippingMethodDiscr(shippingMethod) {
    if (!shippingMethod) {
        return null;
    }
    if (typeof shippingMethod.discr === 'string' && shippingMethod.discr !== '') {
        return shippingMethod.discr;
    }
    const type = shippingMethod['@type'];
    if (typeof type === 'string' && type !== '') {
        return type.charAt(0).toLowerCase() + type.slice(1);
    }

    return null;
}

export function isRelayPointChangeable(shipment) {
    const discr = getShippingMethodDiscr(shipment?.shippingMethod);

    return null !== discr && RelayPointChangeableDiscr.includes(discr);
}
